import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, AlertCircleIcon, XCircleIcon } from 'lucide-react';
interface CancelViewingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  viewing: any;
}
const CANCEL_REASONS = ['Nu mai răspunde chiriașul', 'Nu mai răspunde proprietarul', 'Proprietatea s-a închiriat', 'Chiriașul a găsit alt imobil', 'Chiriașul nu mai dorește colaborare', 'Proprietarul nu mai dorește colaborare', 'Nu am putut susține vizionarea'];
export function CancelViewingModal({
  isOpen,
  onClose,
  onSubmit,
  viewing
}: CancelViewingModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const handleSubmit = () => {
    const reason = selectedReason === 'Nu am putut susține vizionarea' ? customReason : selectedReason;
    if (!reason) {
      alert('Te rog selectează sau introdu un motiv pentru anulare');
      return;
    }
    onSubmit(reason);
    setSelectedReason('');
    setCustomReason('');
  };
  return <AnimatePresence data-id="element-2939">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-2940" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2941">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" data-id="element-2942">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-6 py-5" data-id="element-2943">
                <div className="flex items-center justify-between" data-id="element-2944">
                  <div className="flex items-center space-x-3" data-id="element-2945">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-2946">
                      <XCircleIcon className="w-6 h-6" data-id="element-2947" />
                    </div>
                    <div data-id="element-2948">
                      <h3 className="text-xl font-bold" data-id="element-2949">Anulează Vizionare</h3>
                      <p className="text-sm text-red-100" data-id="element-2950">
                        {viewing?.clientName} - {viewing?.propertyId}
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-2951">
                    <XIcon className="w-6 h-6" data-id="element-2952" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4" data-id="element-2953">
                {/* Warning */}
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl flex items-start space-x-3" data-id="element-2954">
                  <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-2955" />
                  <div data-id="element-2956">
                    <p className="text-sm font-bold text-red-900 mb-1" data-id="element-2957">
                      Atenție: Anulare Vizionare
                    </p>
                    <p className="text-sm text-red-700" data-id="element-2958">
                      Această acțiune va anula vizionarea programată. Te rog
                      indexează motivul anulării pentru evidență.
                    </p>
                  </div>
                </div>

                {/* Reason Selection */}
                <div data-id="element-2959">
                  <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-2960">
                    Motiv Anulare * (Indexare Obligatorie)
                  </label>
                  <div className="space-y-2" data-id="element-2961">
                    {CANCEL_REASONS.map(reason => <label key={reason} className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedReason === reason ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-red-200 hover:bg-slate-50'}`} data-id="element-2962">
                        <input type="radio" name="cancelReason" value={reason} checked={selectedReason === reason} onChange={e => setSelectedReason(e.target.value)} className="w-4 h-4 text-red-600 focus:ring-red-500" data-id="element-2963" />
                        <span className="text-sm font-medium text-slate-700" data-id="element-2964">
                          {reason}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Custom Reason */}
                {selectedReason === 'Nu am putut susține vizionarea' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} data-id="element-2965">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2966">
                      Specifică Motivul *
                    </label>
                    <textarea value={customReason} onChange={e => setCustomReason(e.target.value)} rows={3} placeholder="Descrie motivul pentru care nu ai putut susține vizionarea..." className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none" data-id="element-2967" />
                  </motion.div>}
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200" data-id="element-2968">
                <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-semibold text-slate-700 hover:bg-slate-200 transition-colors" data-id="element-2969">
                  Renunță
                </button>
                <button onClick={handleSubmit} className="px-6 py-2.5 rounded-xl font-bold bg-gradient-to-r from-red-500 to-rose-600 text-white hover:shadow-lg hover:scale-105 transition-all" data-id="element-2970">
                  Confirmă Anulare
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}